import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import IconComponent from '../../-private/components/link-or-button-icon.gts';
import type { Signature as IconSignature } from '../../-private/components/link-or-button-icon';
export interface WrButtonSignature {
    Element: HTMLButtonElement;
    Args: {
        // See https://steveholgado.com/typescript-types-from-arrays to understand this type
        variant?: (typeof VALID_VARIANTS)[number];
        icon?: IconSignature['Args']['icon'];
        ariaLabel?: string;
    };
    Blocks: {
        default: [];
    };
}
const VALID_VARIANTS = [
    'outlined',
    'primary',
    'anchor',
    'inverted anchor',
    'outlined-mini',
    'primary-white-border'
] as const; // See [const assertions](https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-4.html#const-assertions) to know why we need `as const`
const BUTTON_COMMON_STYLES = [
    'active:disabled:bg-none',
    'disabled:bg-white',
    'focus-visible:outline-2',
    'focus-visible:outline-offset-0',
    'focus-visible:outline',
    'font-sans',
    'rounded-md',
    'text-p1'
];
const BUTTON_BASE_STYLES = [
    'border',
    'active:bg-gradient-to-r',
    'disabled:border-grey-medium',
    'disabled:text-grey-medium',
    'hover:disabled:shadow-none',
    'hover:shadow',
    'px-4',
    'py-2'
];
const ANCHOR_BASE_STYLES = [
    'hover:underline',
    'focus-visible:outline-none',
    'focus-visible:text-pink',
    'focus-visible:underline'
];
const PRIMARY_BASE_STYLE = [
    ...BUTTON_COMMON_STYLES,
    ...BUTTON_BASE_STYLES,
    'active:from-purple',
    'active:to-pink',
    'text-white'
];
const STYLES = {
    base: [
        'gap-2',
        'inline-flex',
        'items-center',
        'justify-center'
    ],
    primary: [
        ...PRIMARY_BASE_STYLE,
        'bg-pink',
        'border-pink',
        'focus-visible:border-purple',
        'focus-visible:outline-purple'
    ],
    outlined: [
        ...BUTTON_COMMON_STYLES,
        ...BUTTON_BASE_STYLES,
        '[&>svg]:text-grey',
        'active:from-grey-medium',
        'active:to-white',
        'bg-white',
        'border-grey-medium',
        'focus-visible:border-black',
        'focus-visible:outline-black'
    ],
    'outlined-mini': [
        ...BUTTON_COMMON_STYLES,
        '[&>svg]:text-grey',
        '[&>svg]:w-6',
        '[&>svg]:h-6',
        'active:from-grey-medium',
        'active:to-white',
        'bg-white',
        'hover:bg-grey-medium',
        'focus-visible:outline-black',
        'border-none',
        'p-1'
    ],
    anchor: [
        ...ANCHOR_BASE_STYLES
    ],
    'inverted anchor': [
        ...ANCHOR_BASE_STYLES,
        'text-white',
        'focus-visible:text-purple'
    ],
    'primary-white-border': [
        ...PRIMARY_BASE_STYLE,
        'bg-transparent, border-white'
    ]
} as const;
export default class WrButtonComponent extends Component<WrButtonSignature> {
    get variant() {
        return this.args.variant || 'primary';
    }
    get styles() {
        return [
            ...STYLES.base,
            ...STYLES[this.variant]
        ].join(' ');
    }
    static{
        template(`
    <button
      type='button'
      class={{this.styles}}
      aria-label={{@ariaLabel}}
      ...attributes
    >
      <IconComponent @icon={{@icon}} />
      {{yield}}
    </button>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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-c3ZnXTp0ZXh0LWdyZXknLFxuICAgICdhY3RpdmU6ZnJvbS1ncmV5LW1lZGl1bScsXG4gICAgJ2FjdGl2ZTp0by13aGl0ZScsXG4gICAgJ2JnLXdoaXRlJyxcbiAgICAnYm9yZGVyLWdyZXktbWVkaXVtJyxcbiAgICAnZm9jdXMtdmlzaWJsZTpib3JkZXItYmxhY2snLFxuICAgICdmb2N1cy12aXNpYmxlOm91dGxpbmUtYmxhY2snXG4gIF0sXG4gICdvdXRsaW5lZC1taW5pJzogW1xuICAgIC4uLkJVVFRPTl9DT01NT05fU1RZTEVTLFxuICAgICdbJj5zdmddOnRleHQtZ3JleScsXG4gICAgJ1smPnN2Z106dy02JyxcbiAgICAnWyY-c3ZnXTpoLTYnLFxuICAgICdhY3RpdmU6ZnJvbS1ncmV5LW1lZGl1bScsXG4gICAgJ2FjdGl2ZTp0by13aGl0ZScsXG4gICAgJ2JnLXdoaXRlJyxcbiAgICAnaG92ZXI6YmctZ3JleS1tZWRpdW0nLFxuICAgICdmb2N1cy12aXNpYmxlOm91dGxpbmUtYmxhY2snLFxuICAgICdib3JkZXItbm9uZScsXG4gICAgJ3AtMSdcbiAgXSxcbiAgYW5jaG9yOiBbLi4uQU5DSE9SX0JBU0VfU1RZTEVTXSxcbiAgJ2ludmVydGVkIGFuY2hvcic6IFtcbiAgICAuLi5BTkNIT1JfQkFTRV9TVFlMRVMsXG4gICAgJ3RleHQtd2hpdGUnLFxuICAgICdmb2N1cy12aXNpYmxlOnRleHQtcHVycGxlJ1xuICBdLFxuICAncHJpbWFyeS13aGl0ZS1ib3JkZXInOiBbXG4gICAgLi4uUFJJTUFSWV9CQVNFX1NUWUxFLFxuICAgICdiZy10cmFuc3BhcmVudCwgYm9yZGVyLXdoaXRlJ1xuICBdXG59IGFzIGNvbnN0O1xuXG5leHBvcnQgZGVmYXVsdCBjbGFzcyBXckJ1dHRvbkNvbXBvbmVudCBleHRlbmRzIENvbXBvbmVudDxXckJ1dHRvblNpZ25hdHVyZT4ge1xuICBnZXQgdmFyaWFudCgpIHtcbiAgICByZXR1cm4gdGhpcy5hcmdzLnZhcmlhbnQgfHwgJ3ByaW1hcnknO1xuICB9XG5cbiAgZ2V0IHN0eWxlcygpIHtcbiAgICByZXR1cm4gWy4uLlNUWUxFUy5iYXNlLCAuLi5TVFlMRVNbdGhpcy52YXJpYW50XV0uam9pbignICcpO1xuICB9XG5cbiAgPHRlbXBsYXRlPlxuICAgIDxidXR0b25cbiAgICAgIHR5cGU9J2J1dHRvbidcbiAgICAgIGNsYXNzPXt7dGhpcy5zdHlsZXN9fVxuICAgICAgYXJpYS1sYWJlbD17e0BhcmlhTGFiZWx9fVxuICAgICAgLi4uYXR0cmlidXRlc1xuICAgID5cbiAgICAgIDxJY29uQ29tcG9uZW50IEBpY29uPXt7QGljb259fSAvPlxuICAgICAge3t5aWVsZH19XG4gICAgPC9idXR0b24-XG4gIDwvdGVtcGxhdGU-XG59XG4iXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IjtBQUFBLE9BQU8sZUFBZSxxQkFBcUI7QUFDM0MsT0FBTyxtQkFBbUIsb0RBQW9EO0FBQzlFLGNBQWMsYUFBYSxhQUFhLFFBQVEsZ0RBQWdEO0FBRWhHLGlCQUFpQjtJQUNmLFNBQVM7SUFDVDtRQUNFLG9GQUFvRjtRQUNwRixrQkFBa0IsZ0JBQWdCLE1BQU07UUFDeEMsT0FBTyxjQUFjLFFBQVE7UUFDN0IsWUFBWSxNQUFNOztJQUVwQjtRQUNFOzs7QUFJSixNQUFNLGlCQUFpQjtJQUNyQjtJQUNBO0lBQ0E7SUFDQTtJQUNBO0lBQ0E7Q0FDRCxXQUFXLHlKQUF5SjtBQUVySyxNQUFNLHVCQUF1QjtJQUMzQjtJQUNBO0lBQ0E7SUFDQTtJQUNBO0lBQ0E7SUFDQTtJQUNBO0NBQ0Q7QUFFRCxNQUFNLHFCQUFxQjtJQUN6QjtJQUNBO0lBQ0E7SUFDQTtJQUNBO0lBQ0E7SUFDQTtJQUNBO0NBQ0Q7QUFFRCxNQUFNLHFCQUFxQjtJQUN6QjtJQUNBO0lBQ0E7SUFDQTtDQUNEO0FBRUQsTUFBTSxxQkFBcUI7T0FDdEI7T0FDQTtJQUNIO0lBQ0E7SUFDQTtDQUNEO0FBQ0QsTUFBTSxTQUFTO0lBQ2IsTUFBTTtRQUFDO1FBQVM7UUFBZTtRQUFnQjtLQUFpQjtJQUNoRSxTQUFTO1dBQ0o7UUFDSDtRQUNBO1FBQ0E7UUFDQTtLQUNEO0lBQ0QsVUFBVTtXQUNMO1dBQ0E7UUFDSDtRQUNBO1FBQ0E7UUFDQTtRQUNBO1FBQ0E7UUFDQTtLQUNEO0lBQ0QsaUJBQWlCO1dBQ1o7UUFDSDtRQUNBO1FBQ0E7UUFDQTtRQUNBO1FBQ0E7UUFDQTtRQUNBO1FBQ0E7UUFDQTtLQUNEO0lBQ0QsUUFBUTtXQUFJO0tBQW1CO0lBQy9CLG1CQUFtQjtXQUNkO1FBQ0g7UUFDQTtLQUNEO0lBQ0Qsd0JBQXdCO1dBQ25CO1FBQ0g7S0FDRDtBQUNIO0FBRUEsZUFBZSxNQUFNLDBCQUEwQixVQUFVO0lBQ3ZELElBQUksVUFBVTtRQUNaLE9BQU8sSUFBSSxDQUFDLElBQUksQ0FBQyxPQUFPLElBQUk7SUFDOUI7SUFFQSxJQUFJLFNBQVM7UUFDWCxPQUFPO2VBQUksT0FBTyxJQUFJO2VBQUssTUFBTSxDQUFDLElBQUksQ0FBQyxPQUFPLENBQUM7U0FBQyxDQUFDLElBQUksQ0FBQztJQUN4RDtJQUVBLE1BQUE7UUFBQSxTQUFVLENBQUE7Ozs7Ozs7Ozs7RUFVVixDQUFBLEVBQUE7WUFBQSxXQUFBLElBQVc7WUFBWDtnQkFBQSxPQUFBLEtBQUEsU0FBQSxDQUFBLEVBQVc7WUFBRDtRQUFBO0lBQUEsQ0FWQTtBQVdaIn0